// src/hooks/useMenuData.js
// ─────────────────────────────────────────────────────────────────────────────
// Fix #2 — Custom Hook: useMenuData
//
// Manages: menu (categories + items), riders, loading state
// Extracted from: sale.js (was mixed with orders + customers + UI state)
//
// BENEFIT: Menu re-renders are now isolated. Changing an order or customer
// will NOT trigger menu component re-renders.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback, useEffect, useRef } from 'react';
import { saas } from '../supabaseClient';
import { parseRecipeQuantity } from '../services/recipeUnits';
import { buildInventoryDisplayName, normalizeInventoryProfile } from '../services/inventoryDisplay';

const normalizeInventoryItem = (item) => ({
  ...normalizeInventoryProfile(item),
  current_stock: parseFloat(item.stock_level ?? 0) || 0,
  stock_level: parseFloat(item.stock_level ?? 0) || 0,
});

const normalizeName = (value) => String(value || '').trim().toLowerCase();
const getCurrentTenantId = () => localStorage.getItem('currentRestaurantId') || '';

const filterRowsByTenant = (rows = []) => {
  const tenantId = getCurrentTenantId();
  if (!tenantId) return rows;

  return rows.filter(row => (
    !row?.restaurant_id || String(row.restaurant_id) === String(tenantId)
  ));
};

const buildInventoryCostRates = (purchaseRows = [], inventoryRows = []) => {
  const inventoryByName = new Map(
    inventoryRows.map(item => [normalizeName(item.name), item])
  );
  const rates = {};

  purchaseRows.forEach(row => {
    if (row.is_reversed) return;
    const rate = parseFloat(row.rate ?? 0);
    if (!Number.isFinite(rate) || rate <= 0) return;

    const inventoryId = row.inventory_item_id || inventoryByName.get(normalizeName(row.item))?.id;
    if (!inventoryId) return;

    const key = String(inventoryId);
    if (rates[key]) return;

    rates[key] = {
      rate,
      item: row.item,
      date: row.date,
    };
  });

  return rates;
};

const CATEGORY_COLUMNS = 'id, name, show_on_public_store, public_sort_order';
const CATEGORY_FALLBACK_COLUMNS = 'id, name';
const MENU_ITEM_COLUMNS = 'id, name, price, description, image_url, business_category, category_id, is_available, is_out_of_stock, has_variants';
const RIDER_COLUMNS = 'id, name, balance, restaurant_id';
const VARIANT_COLUMNS = 'id, menu_item_id, name, price, is_available, is_out_of_stock';
const MODIFIER_GROUP_COLUMNS = 'id, name, min_selection, max_selection';
const MODIFIER_ITEM_COLUMNS = 'id, group_id, name, price, is_available, is_out_of_stock';
const MODIFIER_LINK_COLUMNS = 'id, menu_item_id, modifier_group_id';
const INVENTORY_COLUMNS = 'id, name, brand, size_label, item_notes, display_name, search_name, unit, stock_level, min_threshold, last_payment_type, business_category';
const RECIPE_COLUMNS = 'id, menu_item_id, variant_id, modifier_id, inventory_item_id, ingredient_name, quantity_required';
const PREP_RECIPE_COLUMNS = 'id, name, description, yield_qty, yield_unit, output_inventory_item_id, is_active, created_at, updated_at';
const PREP_RECIPE_INGREDIENT_COLUMNS = 'id, prep_recipe_id, inventory_item_id, ingredient_name, quantity, unit, ingredient_role, sort_order';
const PREP_RECIPE_INGREDIENT_FALLBACK_COLUMNS = 'id, prep_recipe_id, inventory_item_id, ingredient_name, quantity, unit, sort_order';
const RECIPE_TEMPLATE_COLUMNS = 'id, name, description, template_type, costing_method, is_active, created_at, updated_at';
const RECIPE_TEMPLATE_INGREDIENT_COLUMNS = 'id, template_id, ingredient_type, inventory_item_id, prep_recipe_id, ingredient_name, quantity, unit, wastage_percent, sort_order';
const RECIPE_TEMPLATE_ASSIGNMENT_COLUMNS = 'id, template_id, menu_item_id, variant_id, modifier_id, assignment_mode, is_active, created_at';
const PURCHASE_RATE_COLUMNS = 'id, inventory_item_id, item, rate, date, is_reversed';
const PURCHASE_RATE_FALLBACK_COLUMNS = 'id, item, rate, date, is_reversed';

export const useMenuData = (options = {}) => {
  const { onError } = options;
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const reportErrorRef = useRef((message) => {
    console.error(message);
  });

  useEffect(() => {
    reportErrorRef.current = (message) => {
      console.error(message);
      onErrorRef.current?.(message);
    };
  }, []);
  const [menu, setMenu] = useState({});
  const [categoryMeta, setCategoryMeta] = useState([]);
  const [riders, setRiders] = useState([]);
  const [modifierGroups, setModifierGroups] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [prepRecipes, setPrepRecipes] = useState([]);
  const [prepRecipeIngredients, setPrepRecipeIngredients] = useState([]);
  const [recipeTemplates, setRecipeTemplates] = useState([]);
  const [recipeTemplateIngredients, setRecipeTemplateIngredients] = useState([]);
  const [recipeTemplateAssignments, setRecipeTemplateAssignments] = useState([]);
  const [inventoryCostRates, setInventoryCostRates] = useState({});
  const [activeCategory, setActiveCategory] = useState('');
  const [menuLoading, setMenuLoading] = useState(true);

  // ── Populate menu from raw DB data (called by parent fetchData) ────────────
  const buildMenu = useCallback((catData, menuDataRaw, variantsRaw, modGroupsRaw, modItemsRaw, modLinksRaw) => {
    const formattedMenu = {};
    const categorizedItemIds = new Set();
    const itemsByCategory = new Map();
    const variantsByItem = new Map();
    const modifierGroupsById = new Map();
    const modifierItemsByGroup = new Map();
    const modifierGroupIdsByItem = new Map();

    (menuDataRaw || []).forEach(item => {
      const categoryItems = itemsByCategory.get(item.category_id) || [];
      categoryItems.push(item);
      itemsByCategory.set(item.category_id, categoryItems);
    });

    (variantsRaw || []).forEach(variant => {
      const itemVariants = variantsByItem.get(variant.menu_item_id) || [];
      itemVariants.push(variant);
      variantsByItem.set(variant.menu_item_id, itemVariants);
    });

    (modGroupsRaw || []).forEach(group => {
      modifierGroupsById.set(group.id, group);
    });

    (modItemsRaw || []).forEach(item => {
      const groupItems = modifierItemsByGroup.get(item.group_id) || [];
      groupItems.push(item);
      modifierItemsByGroup.set(item.group_id, groupItems);
    });

    (modLinksRaw || []).forEach(link => {
      const groupIds = modifierGroupIdsByItem.get(link.menu_item_id) || [];
      groupIds.push(link.modifier_group_id);
      modifierGroupIdsByItem.set(link.menu_item_id, groupIds);
    });

    // Helper to nest variants and modifiers into items
    const processItems = (items) => items.map(item => {
      categorizedItemIds.add(item.id);

      // Get variants for this item
      const variants = [...(variantsByItem.get(item.id) || [])]
        .sort((a, b) => Number(a.price || 0) - Number(b.price || 0));

      // Get modifier groups for this item via links
      const itemModGroupIds = modifierGroupIdsByItem.get(item.id) || [];
      const modifierGroups = itemModGroupIds
        .map(groupId => modifierGroupsById.get(groupId))
        .filter(Boolean)
        .map(group => ({
          ...group,
          items: modifierItemsByGroup.get(group.id) || []
        }));

      return {
        id: item.id,
        name: item.name,
        price: item.price,
        description: item.description,
        businessCategory: item.business_category,
        image_url: item.image_url,
        is_available: item.is_available,
        is_out_of_stock: item.is_out_of_stock,
        has_variants: item.has_variants,
        variants,
        modifierGroups,
      };
    });

    if (catData) {
      setCategoryMeta(catData.map(cat => ({
        id: cat.id,
        name: cat.name,
        show_on_public_store: cat.show_on_public_store !== false,
        public_sort_order: cat.public_sort_order ?? null,
      })));

      catData.forEach(cat => {
        const items = itemsByCategory.get(cat.id) || [];
        formattedMenu[cat.name] = processItems(items);
      });
    }

    // Orphaned items
    const orphaned = (menuDataRaw || []).filter(i => !categorizedItemIds.has(i.id));
    if (orphaned.length > 0) {
      formattedMenu['Uncategorized'] = processItems(orphaned);
    }

    setMenu(formattedMenu);
    setMenuLoading(false);
  }, []);

  // ── Set riders from raw DB data ───────────────────────────────────────────
  const buildRiders = useCallback((riderList) => {
    const seen = new Set();
    const scopedRiders = filterRowsByTenant(riderList || [])
      .filter((rider) => {
        const key = String(rider.id || rider.name || '').trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    setRiders(scopedRiders);
  }, []);

  // ── Auto-fetch on mount ──────────────────────────────────────────────────
  const fetchMenuData = useCallback(async ({ showLoading = true } = {}) => {
    if (showLoading) setMenuLoading(true);
    try {
      let [
        catResult, menuResult, riderResult,
        variantResult, modGroupResult, 
        modItemResult, modLinkResult,
        invResult, recipeResult, prepRecipeResult, prepIngredientResult,
        templateResult, templateIngredientResult, templateAssignmentResult
      ] = await Promise.all([
        saas('categories').select(CATEGORY_COLUMNS).order('name'),
        saas('menu_items').select(MENU_ITEM_COLUMNS),
        saas('riders').select(RIDER_COLUMNS).order('name'),
        saas('item_variants').select(VARIANT_COLUMNS),
        saas('modifier_groups').select(MODIFIER_GROUP_COLUMNS),
        saas('modifier_items').select(MODIFIER_ITEM_COLUMNS),
        saas('item_modifier_links').select(MODIFIER_LINK_COLUMNS),
        saas('inventory_items').select(INVENTORY_COLUMNS).order('name'),
        saas('menu_item_recipes').select(RECIPE_COLUMNS),
        saas('prep_recipes').select(PREP_RECIPE_COLUMNS).eq('is_active', true).order('name'),
        saas('prep_recipe_ingredients').select(PREP_RECIPE_INGREDIENT_COLUMNS).order('sort_order'),
        saas('menu_recipe_templates').select(RECIPE_TEMPLATE_COLUMNS).eq('is_active', true).order('name'),
        saas('menu_recipe_template_ingredients').select(RECIPE_TEMPLATE_INGREDIENT_COLUMNS).order('sort_order'),
        saas('menu_recipe_template_assignments').select(RECIPE_TEMPLATE_ASSIGNMENT_COLUMNS).eq('is_active', true),
      ]);

      if (
        catResult.error?.message?.includes('show_on_public_store') ||
        catResult.error?.message?.includes('public_sort_order')
      ) {
        catResult = await saas('categories').select(CATEGORY_FALLBACK_COLUMNS).order('name');
      }

      if (prepIngredientResult.error?.message?.includes('ingredient_role')) {
        prepIngredientResult = await saas('prep_recipe_ingredients')
          .select(PREP_RECIPE_INGREDIENT_FALLBACK_COLUMNS)
          .order('sort_order');
      }

      const failedResult = [
        catResult, menuResult, riderResult, variantResult, modGroupResult,
        modItemResult, modLinkResult, invResult, recipeResult, prepRecipeResult, prepIngredientResult,
        templateResult, templateIngredientResult, templateAssignmentResult
      ].find(result => result.error);
      if (failedResult?.error) throw failedResult.error;

      buildMenu(
        catResult.data, menuResult.data, variantResult.data, 
        modGroupResult.data, modItemResult.data, modLinkResult.data
      );
      buildRiders(riderResult.data);

      setModifierGroups(modGroupResult.data?.map(group => ({
        ...group,
        items: modItemResult.data?.filter(i => i.group_id === group.id) || []
      })) || []);

      setInventoryItems((invResult.data || []).map(normalizeInventoryItem));
      setRecipes(recipeResult.data || []);
      setPrepRecipes(prepRecipeResult.data || []);
      setPrepRecipeIngredients((prepIngredientResult.data || []).map(row => ({
        ...row,
        ingredient_role: row.ingredient_role || 'additive',
      })));
      setRecipeTemplates(templateResult.data || []);
      setRecipeTemplateIngredients(templateIngredientResult.data || []);
      setRecipeTemplateAssignments(templateAssignmentResult.data || []);

      let purchaseRateResult = await saas('purchase_history')
        .select(PURCHASE_RATE_COLUMNS)
        .order('date', { ascending: false })
        .order('id', { ascending: false })
        .limit(2000);

      if (purchaseRateResult.error?.message?.includes('inventory_item_id')) {
        purchaseRateResult = await saas('purchase_history')
          .select(PURCHASE_RATE_FALLBACK_COLUMNS)
          .order('date', { ascending: false })
          .order('id', { ascending: false })
          .limit(2000);
      }

      if (purchaseRateResult.error) {
        console.warn('Recipe costing rates unavailable:', purchaseRateResult.error.message);
        setInventoryCostRates({});
      } else {
        setInventoryCostRates(buildInventoryCostRates(purchaseRateResult.data || [], invResult.data || []));
      }
    } catch (err) {
      console.error('fetchMenuData error:', err);
      reportErrorRef.current('Error loading menu data: ' + err.message);
    } finally {
      if (showLoading) setMenuLoading(false);
    }
  }, [buildMenu, buildRiders]);

  useEffect(() => {
    fetchMenuData();
  }, [fetchMenuData]);

  // ── Auto-select first category when menu loads ────────────────────────────
  useEffect(() => {
    if (Object.keys(menu).length > 0 && !activeCategory)
      setActiveCategory(Object.keys(menu)[0]);
  }, [menu, activeCategory]);

  // ── CRUD: Categories ──────────────────────────────────────────────────────
  const addCategory = useCallback(async (name) => {
    if (menu[name]) { setActiveCategory(name); return true; }
    let { error } = await saas('categories').insert([{ name, show_on_public_store: true }]);
    if (error?.message?.includes('show_on_public_store')) {
      ({ error } = await saas('categories').insert([{ name }]));
    }
    if (error) { reportErrorRef.current('Error adding category: ' + error.message); return false; }
    setMenu(prev => ({ ...prev, [name]: [] }));
    setCategoryMeta(prev => [...prev, { name, show_on_public_store: true, public_sort_order: null }]);
    setActiveCategory(name);
    return true;
  }, [menu]);

  const deleteCategory = useCallback(async (catName) => {
    const { error } = await saas('categories').delete().eq('name', catName);
    if (error) { reportErrorRef.current('Error deleting category: ' + error.message); return false; }
    setMenu(prev => {
      const next = { ...prev };
      delete next[catName];
      return next;
    });
    setCategoryMeta(prev => prev.filter(cat => cat.name !== catName));
    return true;
  }, []);

  const updateCategoryPublicVisibility = useCallback(async (catName, isVisible) => {
    const { error } = await saas('categories')
      .update({ show_on_public_store: Boolean(isVisible) })
      .eq('name', catName);
    if (error) { reportErrorRef.current('Error updating public category visibility: ' + error.message); return false; }
    setCategoryMeta(prev => prev.map(cat => (
      cat.name === catName ? { ...cat, show_on_public_store: Boolean(isVisible) } : cat
    )));
    return true;
  }, []);

  const moveCategoryUp = useCallback(async (catName, refetchCallback) => {
    const keys = Object.keys(menu);
    const index = keys.indexOf(catName);
    if (index <= 0) return;
    const newOrder = [...keys];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    for (let i = 0; i < newOrder.length; i++) {
      const oldName = newOrder[i];
      const newName =
        (i + 1).toString().padStart(2, '0') + '. ' + oldName.replace(/^\d+\.\s+/, '');
      if (oldName !== newName)
        await saas('categories').update({ name: newName }).eq('name', oldName);
    }
    fetchMenuData({ showLoading: false });
    if (refetchCallback) refetchCallback();
  }, [menu, fetchMenuData]);

  // ── CRUD: Items (Advanced) ────────────────────────────────────────────────
  const saveFullItem = useCallback(async (formData) => {
    try {
      const {
        id, name, price, description, category, businessCategory,
        image_url, is_available, has_variants, variants
      } = formData;

      const { data: catData, error: catError } = await saas('categories').select('id').eq('name', category).single();
      if (catError || !catData) throw new Error(catError?.message || 'Category not found');

      const itemToSave = {
        name: name.trim(),
        price: parseFloat(price) || 0,
        description: description || '',
        category_id: catData.id,
        business_category: businessCategory,
        image_url: image_url || '',
        is_available: is_available !== undefined ? is_available : true,
        has_variants: has_variants || false,
      };

      let savedItem;
      if (id) {
        const { data, error } = await saas('menu_items').update(itemToSave).eq('id', id).select();
        if (error) throw error;
        savedItem = data?.[0];
      } else {
        const { data, error } = await saas('menu_items').insert([itemToSave]).select();
        if (error) throw error;
        savedItem = data?.[0];
      }
      if (!savedItem) throw new Error('Menu item was not returned after save');

      const existingVariantsResult = await saas('item_variants').select('id').eq('menu_item_id', savedItem.id);
      if (existingVariantsResult.error) throw existingVariantsResult.error;
      const existingVariantIds = new Set((existingVariantsResult.data || []).map(v => v.id));
      const keptVariantIds = new Set();

      if (has_variants && variants && variants.length > 0) {
        for (const variant of variants) {
          const variantPayload = {
            menu_item_id: savedItem.id,
            name: variant.name,
            price: parseFloat(variant.price) || 0,
            is_available: variant.is_available !== undefined ? variant.is_available : true,
          };

          if (variant.id) {
            const { error } = await saas('item_variants').update(variantPayload).eq('id', variant.id);
            if (error) throw error;
            keptVariantIds.add(variant.id);
          } else {
            const { data, error } = await saas('item_variants').insert([variantPayload]).select('id');
            if (error) throw error;
            if (data?.[0]?.id) keptVariantIds.add(data[0].id);
          }
        }

        const removedVariantIds = [...existingVariantIds].filter(variantId => !keptVariantIds.has(variantId));
        if (removedVariantIds.length > 0) {
          const { error } = await saas('item_variants').delete().in('id', removedVariantIds);
          if (error) throw error;
        }
      } else if (existingVariantIds.size > 0) {
        const { error } = await saas('item_variants').delete().eq('menu_item_id', savedItem.id);
        if (error) throw error;
      }

      const selectedModifierIds = new Set((formData.modifierGroups || []).map(group => group.id).filter(Boolean));
      const existingLinksResult = await saas('item_modifier_links').select('modifier_group_id').eq('menu_item_id', savedItem.id);
      if (existingLinksResult.error) throw existingLinksResult.error;

      const existingModifierIds = new Set((existingLinksResult.data || []).map(link => link.modifier_group_id));
      const linksToInsert = [...selectedModifierIds]
        .filter(groupId => !existingModifierIds.has(groupId))
        .map(groupId => ({
          menu_item_id: savedItem.id,
          modifier_group_id: groupId
        }));
      if (linksToInsert.length > 0) {
        const { error } = await saas('item_modifier_links').insert(linksToInsert);
        if (error) throw error;
      }

      const linksToRemove = [...existingModifierIds].filter(groupId => !selectedModifierIds.has(groupId));
      for (const groupId of linksToRemove) {
        const { error } = await saas('item_modifier_links')
          .delete()
          .eq('menu_item_id', savedItem.id)
          .eq('modifier_group_id', groupId);
        if (error) throw error;
      }

      fetchMenuData({ showLoading: false });
      return savedItem;
    } catch (error) {
      reportErrorRef.current('Error saving menu item: ' + error.message);
      return null;
    }
  }, [fetchMenuData]);

  const updateItemImage = useCallback(async (itemId, category, imageUrl) => {
    const { error } = await saas('menu_items').update({ image_url: imageUrl }).eq('id', itemId);
    if (error) { reportErrorRef.current('Error updating image: ' + error.message); return false; }
    setMenu(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(cat => {
        next[cat] = next[cat].map(item => item.id === itemId ? { ...item, image_url: imageUrl } : item);
      });
      return next;
    });
    return true;
  }, []);

  const deleteItem = useCallback(async (itemId, category) => {
    const { error } = await saas('menu_items').delete().eq('id', itemId);
    if (error) { reportErrorRef.current('Error deleting: ' + error.message); return false; }
    setMenu(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(cat => {
        next[cat] = next[cat].filter(item => item.id !== itemId);
      });
      return next;
    });
    return true;
  }, []);

  const updateItemPrice = useCallback(async (itemId, category, newPrice) => {
    const price = parseFloat(newPrice);
    if (isNaN(price)) return;
    const { error } = await saas('menu_items').update({ price }).eq('id', itemId);
    if (error) { reportErrorRef.current('Error updating price: ' + error.message); return false; }
    setMenu(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(cat => {
        next[cat] = next[cat].map(item => item.id === itemId ? { ...item, price } : item);
      });
      return next;
    });
    return true;
  }, []);

  const updateItemAvailability = useCallback(async (itemId, category, isAvailable) => {
    const { error } = await saas('menu_items').update({ is_available: isAvailable }).eq('id', itemId);
    if (error) { reportErrorRef.current('Error updating availability: ' + error.message); return false; }
    setMenu(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(cat => {
        next[cat] = next[cat].map(item => item.id === itemId ? { ...item, is_available: isAvailable } : item);
      });
      return next;
    });
    return true;
  }, []);

  // ── CRUD: Modifier Groups ────────────────────────────────────────────────
  const addModifierGroup = useCallback(async (name, min = 0, max = 1) => {
    const { data, error } = await saas('modifier_groups').insert([{ 
      name, 
      min_selection: min, 
      max_selection: max 
    }]).select();
    if (error) { reportErrorRef.current('Error adding group: ' + error.message); return false; }
    if (data?.[0]) setModifierGroups(prev => [...prev, { ...data[0], items: [] }]);
    return true;
  }, []);

  const deleteModifierGroup = useCallback(async (id) => {
    if (!window.confirm('Delete this modifier group?')) return;
    const { error } = await saas('modifier_groups').delete().eq('id', id);
    if (error) { reportErrorRef.current('Error deleting group: ' + error.message); return false; }
    setModifierGroups(prev => prev.filter(group => group.id !== id));
    setMenu(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(cat => {
        next[cat] = next[cat].map(item => ({
          ...item,
          modifierGroups: (item.modifierGroups || []).filter(group => group.id !== id)
        }));
      });
      return next;
    });
    return true;
  }, []);

  const addModifierItem = useCallback(async (groupId, name, price = 0, stockLink = null) => {
    const { data, error } = await saas('modifier_items').insert([{ 
      group_id: groupId, 
      name, 
      price: parseFloat(price) || 0 
    }]).select();
    if (error) { reportErrorRef.current('Error adding modifier item: ' + error.message); return false; }
    if (data?.[0]) {
      const newItem = data[0];
      if (stockLink?.inventory_item_id) {
        const linkedInventoryItem = inventoryItems.find(item => String(item.id) === String(stockLink.inventory_item_id));
        const parsedQuantity = parseRecipeQuantity(stockLink.quantity_required || 1);
        if (!parsedQuantity.ok) {
          reportErrorRef.current(parsedQuantity.message);
        } else {
          const { data: recipeData, error: recipeError } = await saas('menu_item_recipes').insert([{
            menu_item_id: null,
            variant_id: null,
            modifier_id: newItem.id,
            inventory_item_id: Number(stockLink.inventory_item_id),
            ingredient_name: buildInventoryDisplayName(linkedInventoryItem) || name,
            quantity_required: parsedQuantity.value,
          }]).select(RECIPE_COLUMNS).maybeSingle();
          if (recipeError) {
            reportErrorRef.current('Modifier option saved, but stock link failed: ' + recipeError.message);
          } else if (recipeData) {
            setRecipes(prev => [...prev, recipeData]);
          }
        }
      }
      setModifierGroups(prev => prev.map(group => (
        group.id === groupId ? { ...group, items: [...(group.items || []), newItem] } : group
      )));
      setMenu(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(cat => {
          next[cat] = next[cat].map(item => ({
            ...item,
            modifierGroups: (item.modifierGroups || []).map(group => (
              group.id === groupId ? { ...group, items: [...(group.items || []), newItem] } : group
            ))
          }));
        });
        return next;
      });
    }
    return true;
  }, [inventoryItems]);

  const deleteModifierItem = useCallback(async (id) => {
    const { error } = await saas('modifier_items').delete().eq('id', id);
    if (error) { reportErrorRef.current('Error deleting item: ' + error.message); return false; }
    setModifierGroups(prev => prev.map(group => ({
      ...group,
      items: (group.items || []).filter(item => item.id !== id)
    })));
    setMenu(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(cat => {
        next[cat] = next[cat].map(item => ({
          ...item,
          modifierGroups: (item.modifierGroups || []).map(group => ({
            ...group,
            items: (group.items || []).filter(modifierItem => modifierItem.id !== id)
          }))
        }));
      });
      return next;
    });
    return true;
  }, []);

  const linkModifierToItem = useCallback(async (itemId, groupId) => {
    const { error } = await saas('item_modifier_links').insert([{ 
      menu_item_id: itemId, 
      modifier_group_id: groupId 
    }]);
    if (error) { reportErrorRef.current('Error linking: ' + error.message); return false; }
    fetchMenuData({ showLoading: false });
    return true;
  }, [fetchMenuData]);

  const unlinkModifierFromItem = useCallback(async (itemId, groupId) => {
    const { error } = await saas('item_modifier_links')
      .delete()
      .eq('menu_item_id', itemId)
      .eq('modifier_group_id', groupId);
    if (error) { reportErrorRef.current('Error unlinking: ' + error.message); return false; }
    fetchMenuData({ showLoading: false });
    return true;
  }, [fetchMenuData]);

  // ── CRUD: Recipes ────────────────────────────────────────────────────────
  const saveRecipe = useCallback(async (recipeData) => {
    const { id, menu_item_id, variant_id, modifier_id, inventory_item_id, quantity_required } = recipeData;
    const linkedInventoryItem = inventoryItems.find(item => item.id === inventory_item_id);
    const parsedQuantity = parseRecipeQuantity(quantity_required);
    if (!parsedQuantity.ok) {
      reportErrorRef.current(parsedQuantity.message);
      return false;
    }

    const payload = {
      menu_item_id,
      variant_id,
      modifier_id,
      inventory_item_id,
      ingredient_name: recipeData.ingredient_name || buildInventoryDisplayName(linkedInventoryItem) || 'Unknown Ingredient',
      quantity_required: parsedQuantity.value
    };

    if (id) {
      const { error } = await saas('menu_item_recipes').update(payload).eq('id', id);
      if (error) { reportErrorRef.current('Error updating recipe: ' + error.message); return false; }
      setRecipes(prev => prev.map(recipe => recipe.id === id ? { ...recipe, ...payload } : recipe));
    } else {
      const { data, error } = await saas('menu_item_recipes').insert([payload]).select();
      if (error) { reportErrorRef.current('Error adding recipe: ' + error.message); return false; }
      if (data?.[0]) setRecipes(prev => [...prev, data[0]]);
    }
    return true;
  }, [inventoryItems]);

  const deleteRecipe = useCallback(async (id) => {
    const { error } = await saas('menu_item_recipes').delete().eq('id', id);
    if (error) { reportErrorRef.current('Error deleting recipe: ' + error.message); return false; }
    setRecipes(prev => prev.filter(recipe => recipe.id !== id));
    return true;
  }, []);

  // --- CRUD: Prep Recipes ---
  const savePrepRecipe = useCallback(async (prepRecipeData) => {
    const parsedYield = parseRecipeQuantity(prepRecipeData.yield_qty);
    if (!parsedYield.ok) {
      reportErrorRef.current(parsedYield.message);
      return null;
    }

    const name = String(prepRecipeData.name || '').trim();
    if (!name) {
      reportErrorRef.current('Prep recipe name is required.');
      return null;
    }

    const payload = {
      name,
      description: prepRecipeData.description || '',
      yield_qty: parsedYield.value,
      yield_unit: String(prepRecipeData.yield_unit || 'kg').trim() || 'kg',
      output_inventory_item_id: prepRecipeData.output_inventory_item_id || null,
      is_active: true
    };

    if (prepRecipeData.id) {
      const { data, error } = await saas('prep_recipes')
        .update(payload)
        .eq('id', prepRecipeData.id)
        .select(PREP_RECIPE_COLUMNS);
      if (error) { reportErrorRef.current('Error saving prep recipe: ' + error.message); return null; }
      const saved = data?.[0] || { ...prepRecipeData, ...payload };
      setPrepRecipes(prev => prev.map(recipe => recipe.id === prepRecipeData.id ? saved : recipe));
      return saved;
    }

    const { data, error } = await saas('prep_recipes')
      .insert([payload])
      .select(PREP_RECIPE_COLUMNS);
    if (error) { reportErrorRef.current('Error adding prep recipe: ' + error.message); return null; }
    const saved = data?.[0];
    if (saved) setPrepRecipes(prev => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
    return saved || null;
  }, []);

  const archivePrepRecipe = useCallback(async (id) => {
    if (!window.confirm('Archive this prep recipe? Existing records will remain, but it will hide from active recipe setup.')) return false;
    const { error } = await saas('prep_recipes')
      .update({ is_active: false, archived_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { reportErrorRef.current('Error archiving prep recipe: ' + error.message); return false; }
    setPrepRecipes(prev => prev.filter(recipe => recipe.id !== id));
    setPrepRecipeIngredients(prev => prev.filter(row => row.prep_recipe_id !== id));
    return true;
  }, []);

  const savePrepRecipeIngredient = useCallback(async (ingredientData) => {
    const parsedQuantity = parseRecipeQuantity(ingredientData.quantity);
    if (!parsedQuantity.ok) {
      reportErrorRef.current(parsedQuantity.message);
      return false;
    }

    const linkedInventoryItem = inventoryItems.find(item => item.id === ingredientData.inventory_item_id);
    const payload = {
      prep_recipe_id: ingredientData.prep_recipe_id,
      inventory_item_id: ingredientData.inventory_item_id,
      ingredient_name: ingredientData.ingredient_name || buildInventoryDisplayName(linkedInventoryItem) || 'Unknown Ingredient',
      quantity: parsedQuantity.value,
      unit: ingredientData.unit || linkedInventoryItem?.unit || 'pcs',
      ingredient_role: ingredientData.ingredient_role || 'additive',
      sort_order: ingredientData.sort_order || 0
    };

    if (!payload.prep_recipe_id || !payload.inventory_item_id) {
      reportErrorRef.current('Prep recipe and inventory item are required.');
      return false;
    }

    if (ingredientData.id) {
      let { error } = await saas('prep_recipe_ingredients').update(payload).eq('id', ingredientData.id);
      if (error?.message?.includes('ingredient_role')) {
        const { ingredient_role, ...fallbackPayload } = payload;
        const fallbackResult = await saas('prep_recipe_ingredients').update(fallbackPayload).eq('id', ingredientData.id);
        error = fallbackResult.error;
      }
      if (error) { reportErrorRef.current('Error updating prep ingredient: ' + error.message); return false; }
      setPrepRecipeIngredients(prev => prev.map(row => String(row.id) === String(ingredientData.id) ? { ...row, ...payload } : row));
    } else {
      let { data, error } = await saas('prep_recipe_ingredients')
        .insert([payload])
        .select(PREP_RECIPE_INGREDIENT_COLUMNS);
      if (error?.message?.includes('ingredient_role')) {
        const { ingredient_role, ...fallbackPayload } = payload;
        const fallbackResult = await saas('prep_recipe_ingredients')
          .insert([fallbackPayload])
          .select(PREP_RECIPE_INGREDIENT_FALLBACK_COLUMNS);
        data = fallbackResult.data?.map(row => ({ ...row, ingredient_role })) || null;
        error = fallbackResult.error;
      }
      if (error) { reportErrorRef.current('Error adding prep ingredient: ' + error.message); return false; }
      if (data?.[0]) {
        setPrepRecipeIngredients(prev => {
          const withoutDuplicate = prev.filter(row => String(row.id) !== String(data[0].id));
          return [...withoutDuplicate, data[0]];
        });
      } else {
        fetchMenuData({ showLoading: false });
      }
    }
    return true;
  }, [fetchMenuData, inventoryItems]);

  const deletePrepRecipeIngredient = useCallback(async (id) => {
    const { error } = await saas('prep_recipe_ingredients').delete().eq('id', id);
    if (error) { reportErrorRef.current('Error deleting prep ingredient: ' + error.message); return false; }
    setPrepRecipeIngredients(prev => prev.filter(row => String(row.id) !== String(id)));
    return true;
  }, []);

  // --- CRUD: Recipe Templates ---
  const saveRecipeTemplate = useCallback(async (templateData) => {
    const name = String(templateData.name || '').trim();
    if (!name) {
      reportErrorRef.current('Template name is required.');
      return null;
    }

    const payload = {
      name,
      description: templateData.description || '',
      template_type: templateData.template_type || 'general',
      costing_method: templateData.costing_method || 'latest_purchase',
      is_active: true,
    };

    if (templateData.id) {
      const { data, error } = await saas('menu_recipe_templates')
        .update(payload)
        .eq('id', templateData.id)
        .select(RECIPE_TEMPLATE_COLUMNS);
      if (error) { reportErrorRef.current('Error saving template: ' + error.message); return null; }
      const saved = data?.[0] || { ...templateData, ...payload };
      setRecipeTemplates(prev => prev.map(template => String(template.id) === String(templateData.id) ? saved : template));
      return saved;
    }

    const { data, error } = await saas('menu_recipe_templates')
      .insert([payload])
      .select(RECIPE_TEMPLATE_COLUMNS);
    if (error) { reportErrorRef.current('Error adding template: ' + error.message); return null; }
    const saved = data?.[0];
    if (saved) setRecipeTemplates(prev => [...prev, saved].sort((a, b) => a.name.localeCompare(b.name)));
    return saved || null;
  }, []);

  const archiveRecipeTemplate = useCallback(async (id) => {
    if (!window.confirm('Archive this recipe template? Existing template ingredients will remain hidden with it.')) return false;
    const { error } = await saas('menu_recipe_templates')
      .update({ is_active: false, archived_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { reportErrorRef.current('Error archiving template: ' + error.message); return false; }
    setRecipeTemplates(prev => prev.filter(template => String(template.id) !== String(id)));
    setRecipeTemplateIngredients(prev => prev.filter(row => String(row.template_id) !== String(id)));
    setRecipeTemplateAssignments(prev => prev.filter(row => String(row.template_id) !== String(id)));
    return true;
  }, []);

  const saveRecipeTemplateIngredient = useCallback(async (ingredientData) => {
    const parsedQuantity = parseRecipeQuantity(ingredientData.quantity);
    if (!parsedQuantity.ok) {
      reportErrorRef.current(parsedQuantity.message);
      return false;
    }

    const ingredientType = ingredientData.ingredient_type || 'inventory_item';
    const linkedInventoryItem = inventoryItems.find(item => String(item.id) === String(ingredientData.inventory_item_id));
    const linkedPrepRecipe = prepRecipes.find(recipe => String(recipe.id) === String(ingredientData.prep_recipe_id));
    const ingredientName = ingredientData.ingredient_name || (
      ingredientType === 'prep_recipe' ? linkedPrepRecipe?.name : buildInventoryDisplayName(linkedInventoryItem)
    ) || 'Unknown Ingredient';

    const payload = {
      template_id: ingredientData.template_id,
      ingredient_type: ingredientType,
      inventory_item_id: ingredientType === 'inventory_item' ? ingredientData.inventory_item_id : null,
      prep_recipe_id: ingredientType === 'prep_recipe' ? ingredientData.prep_recipe_id : null,
      ingredient_name: ingredientName,
      quantity: parsedQuantity.value,
      unit: ingredientData.unit || (ingredientType === 'prep_recipe' ? linkedPrepRecipe?.yield_unit : linkedInventoryItem?.unit) || 'pcs',
      wastage_percent: Number(ingredientData.wastage_percent || 0),
      sort_order: ingredientData.sort_order || 0,
    };

    if (!payload.template_id || (!payload.inventory_item_id && !payload.prep_recipe_id)) {
      reportErrorRef.current('Template and ingredient are required.');
      return false;
    }

    if (ingredientData.id) {
      const { error } = await saas('menu_recipe_template_ingredients').update(payload).eq('id', ingredientData.id);
      if (error) { reportErrorRef.current('Error updating template ingredient: ' + error.message); return false; }
      setRecipeTemplateIngredients(prev => prev.map(row => String(row.id) === String(ingredientData.id) ? { ...row, ...payload } : row));
    } else {
      const { data, error } = await saas('menu_recipe_template_ingredients')
        .insert([payload])
        .select(RECIPE_TEMPLATE_INGREDIENT_COLUMNS);
      if (error) { reportErrorRef.current('Error adding template ingredient: ' + error.message); return false; }
      if (data?.[0]) setRecipeTemplateIngredients(prev => [...prev.filter(row => String(row.id) !== String(data[0].id)), data[0]]);
    }
    return true;
  }, [inventoryItems, prepRecipes]);

  const deleteRecipeTemplateIngredient = useCallback(async (id) => {
    const { error } = await saas('menu_recipe_template_ingredients').delete().eq('id', id);
    if (error) { reportErrorRef.current('Error deleting template ingredient: ' + error.message); return false; }
    setRecipeTemplateIngredients(prev => prev.filter(row => String(row.id) !== String(id)));
    return true;
  }, []);

  const saveRecipeTemplateAssignment = useCallback(async (assignmentData) => {
    const payload = {
      template_id: assignmentData.template_id,
      menu_item_id: assignmentData.target_type === 'item' ? assignmentData.target_id : null,
      variant_id: assignmentData.target_type === 'variant' ? assignmentData.target_id : null,
      modifier_id: assignmentData.target_type === 'modifier' ? assignmentData.target_id : null,
      assignment_mode: assignmentData.assignment_mode || 'live',
      is_active: true,
    };

    if (!payload.template_id || !assignmentData.target_type || !assignmentData.target_id) {
      reportErrorRef.current('Template and target menu item/variant/modifier are required.');
      return false;
    }

    const { error: archiveError } = await saas('menu_recipe_template_assignments')
      .update({ is_active: false, archived_at: new Date().toISOString() })
      .match({
        ...(payload.menu_item_id ? { menu_item_id: payload.menu_item_id } : {}),
        ...(payload.variant_id ? { variant_id: payload.variant_id } : {}),
        ...(payload.modifier_id ? { modifier_id: payload.modifier_id } : {}),
        is_active: true,
      });
    if (archiveError) { reportErrorRef.current('Error replacing old assignment: ' + archiveError.message); return false; }

    const { data, error } = await saas('menu_recipe_template_assignments')
      .insert([payload])
      .select(RECIPE_TEMPLATE_ASSIGNMENT_COLUMNS);
    if (error) { reportErrorRef.current('Error assigning template: ' + error.message); return false; }
    if (data?.[0]) {
      setRecipeTemplateAssignments(prev => [
        ...prev.filter(row => !(
          (payload.menu_item_id && String(row.menu_item_id) === String(payload.menu_item_id)) ||
          (payload.variant_id && String(row.variant_id) === String(payload.variant_id)) ||
          (payload.modifier_id && String(row.modifier_id) === String(payload.modifier_id))
        )),
        data[0],
      ]);
    }
    return true;
  }, []);

  const archiveRecipeTemplateAssignment = useCallback(async (id) => {
    const { error } = await saas('menu_recipe_template_assignments')
      .update({ is_active: false, archived_at: new Date().toISOString() })
      .eq('id', id);
    if (error) { reportErrorRef.current('Error removing assignment: ' + error.message); return false; }
    setRecipeTemplateAssignments(prev => prev.filter(row => String(row.id) !== String(id)));
    return true;
  }, []);

  return {
    // State
    menu, categoryMeta, riders, modifierGroups, inventoryItems, recipes,
    prepRecipes, prepRecipeIngredients,
    recipeTemplates, recipeTemplateIngredients, recipeTemplateAssignments,
    inventoryCostRates,
    activeCategory, menuLoading,
    // Setters
    setMenu, setRiders, setActiveCategory,
    // Builders
    buildMenu, buildRiders, fetchMenuData,
    // CRUD: Categories & Items
    addCategory, deleteCategory, moveCategoryUp, updateCategoryPublicVisibility,
    addItem: saveFullItem, saveFullItem, updateItemImage, deleteItem, updateItemPrice,
    updateItemAvailability,
    // CRUD: Modifiers
    addModifierGroup, deleteModifierGroup, addModifierItem, deleteModifierItem,
    linkModifierToItem, unlinkModifierFromItem,
    // CRUD: Recipes
    saveRecipe, deleteRecipe,
    savePrepRecipe, archivePrepRecipe, savePrepRecipeIngredient, deletePrepRecipeIngredient,
    saveRecipeTemplate, archiveRecipeTemplate,
    saveRecipeTemplateIngredient, deleteRecipeTemplateIngredient,
    saveRecipeTemplateAssignment, archiveRecipeTemplateAssignment,
    refreshMenu: fetchMenuData
  };
};

